import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, bannerComics, rankingComics, categories } from '@/mocks/comics';

function ComicCard({ comic, featured }: { comic: typeof comics[0]; featured?: boolean }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block cursor-pointer"
    >
      <div className={`relative overflow-hidden rounded-lg bg-background-100 ${featured ? 'aspect-[3/4]' : 'aspect-[3/4]'}`}>
        <img
          src={comic.cover}
          alt={`${comic.title} - ${comic.originalTitle}`}
          title={`${comic.title} 韩国漫画`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <div className="text-white text-xs">
            <p className="font-medium">{comic.author}</p>
            <p className="text-white/70">{comic.views.toLocaleString()} 次阅读</p>
          </div>
        </div>
        {comic.status === '已完结' && (
          <span className="absolute top-2 left-2 bg-secondary-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
            已完结
          </span>
        )}
        {comic.status === '连载中' && (
          <span className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
            连载中
          </span>
        )}
      </div>
      <div className="mt-2.5">
        <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-accent-500 font-semibold">{comic.rating}</span>
          <span className="text-xs text-foreground-400">共{comic.chapters}话</span>
        </div>
      </div>
    </Link>
  );
}

function BannerSlider() {
  const [current, setCurrent] = useState(0);
  const total = bannerComics.length;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
      {bannerComics.map((banner, idx) => (
        <div
          key={banner.slug}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            idx === current ? 'opacity-100 translate-x-0' : idx < current ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full'
          }`}
        >
          <div className="absolute inset-0">
            <img
              src={banner.cover}
              alt={banner.title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/50"></div>
          </div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-[1280px] mx-auto w-full px-4 md:px-6">
              <div className="max-w-lg">
                <div className="flex items-center gap-2 mb-3">
                  {banner.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">{banner.title}</h2>
                <p className="text-xs md:text-sm text-white/70 mb-4">{banner.originalTitle} · 作者：{banner.author}</p>
                <p className="text-sm text-white/80 line-clamp-2 mb-5">{banner.description}</p>
                <div className="flex items-center gap-4">
                  <Link
                    to={`/article/${banner.slug}`}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm px-5 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    立即阅读
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <span className="text-white/70 text-xs">
                    <strong className="text-accent-400">{banner.rating}</strong> 分 · {banner.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-colors cursor-pointer z-20"
        aria-label="Previous banner"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-colors cursor-pointer z-20"
        aria-label="Next banner"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {bannerComics.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              idx === current ? 'bg-white w-6' : 'bg-white/40'
            }`}
            aria-label={`Go to banner ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const featured = comics.filter((c) => c.featured);
  const latest = [...comics].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <BannerSlider />

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">本周最受欢迎的韩国漫画作品</p>
            </div>
            <Link to="/category" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {featured.slice(0, 6).map((comic) => (
              <ComicCard key={comic.id} comic={comic} featured />
            ))}
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">人气排行</h2>
                <p className="text-sm text-foreground-500 mt-1">全站人气最高的漫画作品</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rankingComics.slice(0, 8).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="flex items-center gap-4 bg-background-50 rounded-lg p-4 hover:bg-background-100 transition-colors cursor-pointer group"
                >
                  <span className={`text-2xl font-heading font-bold w-10 text-center ${
                    comic.rank === 1 ? 'text-accent-500' : comic.rank === 2 ? 'text-secondary-500' : comic.rank === 3 ? 'text-primary-400' : 'text-foreground-300'
                  }`}>
                    {String(comic.rank).padStart(2, '0')}
                  </span>
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                    <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{comic.title}</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author} · {comic.category}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-accent-500 font-semibold">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">{comic.chapters}话</span>
                      <span className="text-xs text-foreground-400">{comic.status}</span>
                    </div>
                  </div>
                  <div className="text-foreground-300 group-hover:text-primary-500 transition-colors">
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">漫画分类</h2>
              <p className="text-sm text-foreground-500 mt-1">按照喜好找到最适合你的漫画类型</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="bg-background-100 hover:bg-background-200 rounded-lg p-5 transition-colors cursor-pointer group text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-white transition-all">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">{cat.name}</h3>
                <p className="text-xs text-foreground-400 mt-1">{cat.count}部作品</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最近更新</h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新最新韩漫章节</p>
              </div>
              <Link to="/category" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                全部漫画 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latest.slice(0, 12).map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              开始你的韩漫阅读之旅
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-md mx-auto">
              汗汗漫画网收录海量韩国漫画资源，全部免费在线阅读，每日更新，精彩不容错过！
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 bg-white text-primary-600 text-sm font-medium px-6 py-2.5 rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部漫画
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-white/20 text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">常见问题</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: '汗汗漫画网的漫画都是免费的吗？', a: '是的，汗汗漫画网收录的所有韩国漫画资源均支持免费在线阅读，无需付费即可畅享全部内容。' },
              { q: '漫画多久更新一次？', a: '我们每日更新最新韩漫章节，热门连载作品通常按周更新，具体更新频率取决于原作者的发布节奏。' },
              { q: '支持手机阅读吗？', a: '汗汗漫画网采用响应式设计，完美适配手机、平板和电脑等各种设备，随时随地轻松阅读。' },
              { q: '如何找到我想看的漫画？', a: '你可以通过首页的分类导航、搜索功能或者排行榜快速找到感兴趣的漫画作品。' },
            ].map((faq, i) => (
              <div key={i} className="bg-background-100 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '汗汗漫画网',
            url: 'https://hanhanmanhua.com/',
            description: '汗汗漫画网收录好看的韩国漫画资源，支持韩漫在线免费阅读。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://hanhanmanhua.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '汗汗漫画网',
            url: 'https://hanhanmanhua.com/',
            description: '专注韩国漫画在线免费阅读平台',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@hanhanmanhua.com',
              contactType: 'customer service',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '汗汗漫画网的漫画都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，汗汗漫画网收录的所有韩国漫画资源均支持免费在线阅读，无需付费即可畅享全部内容。' } },
              { '@type': 'Question', name: '漫画多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '我们每日更新最新韩漫章节，热门连载作品通常按周更新，具体更新频率取决于原作者的发布节奏。' } },
              { '@type': 'Question', name: '支持手机阅读吗？', acceptedAnswer: { '@type': 'Answer', text: '汗汗漫画网采用响应式设计，完美适配手机、平板和电脑等各种设备，随时随地轻松阅读。' } },
              { '@type': 'Question', name: '如何找到我想看的漫画？', acceptedAnswer: { '@type': 'Answer', text: '你可以通过首页的分类导航、搜索功能或者排行榜快速找到感兴趣的漫画作品。' } },
            ],
          }),
        }}
      />
    </div>
  );
}